import React from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { MUTATION, QUERY } from '../gql-operations';

import { AuthContext } from '../util/context';
import { CacheManager } from '../util/cacheManager';
import { HIERARCHY } from '../util/specialFolderEnums';
import { DEFAULT_SORT_FIELD } from '../constants/dateField';
import { buildThreadActivityFilterInput } from '../util/threadActivityFilters';
import {
    buildThreadVisibilityFilterInput,
    usesDerivedThreadVisibilityFilter
} from '../util/threadVisibilityFilters';

import useNotificationSystem from '../components/Notifications/useNotificationSystem';

const ensureArray = value => (Array.isArray(value) ? value : []);
const ensureNullable = value => (value === undefined ? null : value);
const ensureNumber = value => (Number.isFinite(value) ? value : 0);

const normalizeMedia = media => {
    if (!media || typeof media !== 'object') {
        return null;
    }

    return {
        ...media,
        url: ensureNullable(media.url),
        cdn_url: ensureNullable(media.cdn_url),
        thumbnail: ensureNullable(media.thumbnail),
        name: ensureNullable(media.name),
        displayName: ensureNullable(media.displayName),
        type: ensureNullable(media.type),
        format: ensureNullable(media.format),
        size: ensureNullable(media.size),
        width: ensureNullable(media.width),
        height: ensureNullable(media.height),
        aspect_ratio: ensureNullable(media.aspect_ratio),
        public_id: ensureNullable(media.public_id),
        duration: ensureNullable(media.duration),
        mime: ensureNullable(media.mime),
        ocrText: ensureNullable(media.ocrText)
    };
};

const normalizeGallerySlide = slide => {
    if (!slide || typeof slide !== 'object') {
        return null;
    }

    return {
        type: ensureNullable(slide.type),
        image: ensureNullable(slide.image),
        image_name: ensureNullable(slide.image_name),
        media_asset_id: ensureNullable(slide.media_asset_id),
        width: ensureNullable(slide.width),
        height: ensureNullable(slide.height),
        aspect_ratio: ensureNullable(slide.aspect_ratio),
        media: normalizeMedia(slide.media),
        media_url: ensureNullable(slide.media_url),
        media_name: ensureNullable(slide.media_name),
        media_cdn_url: ensureNullable(slide.media_cdn_url),
        media_hls_url: ensureNullable(slide.media_hls_url),
        media_duration: ensureNullable(slide.media_duration),
        media_has_audio: ensureNullable(slide.media_has_audio)
    };
};

const normalizeUrlMetaForCacheWrite = meta => {
    if (!meta || typeof meta !== 'object') {
        return null;
    }

    return {
        ...meta,
        author: ensureNullable(meta.author),
        date: ensureNullable(meta.date),
        description: ensureNullable(meta.description),
        image: ensureNullable(meta.image),
        image_asset_id: ensureNullable(meta.image_asset_id),
        gallery_images: ensureArray(meta.gallery_images),
        gallery: ensureArray(meta.gallery).map(normalizeGallerySlide).filter(Boolean),
        image_cdn_url: ensureNullable(meta.image_cdn_url),
        image_name: ensureNullable(meta.image_name),
        media_url: ensureNullable(meta.media_url),
        media_name: ensureNullable(meta.media_name),
        media_asset_id: ensureNullable(meta.media_asset_id),
        media_cdn_url: ensureNullable(meta.media_cdn_url),
        media_type: ensureNullable(meta.media_type),
        media_width: ensureNullable(meta.media_width),
        media_height: ensureNullable(meta.media_height),
        media_duration: ensureNullable(meta.media_duration),
        media_has_audio: ensureNullable(meta.media_has_audio),
        media_audio_url: ensureNullable(meta.media_audio_url),
        media_audio_name: ensureNullable(meta.media_audio_name),
        media_audio_asset_id: ensureNullable(meta.media_audio_asset_id),
        media_hls_url: ensureNullable(meta.media_hls_url),
        logo: ensureNullable(meta.logo),
        publisher: ensureNullable(meta.publisher),
        title: ensureNullable(meta.title),
        url: ensureNullable(meta.url),
        public_id: ensureNullable(meta.public_id),
        width: ensureNullable(meta.width),
        height: ensureNullable(meta.height),
        format: ensureNullable(meta.format),
        aspect_ratio: ensureNullable(meta.aspect_ratio),
        size: ensureNullable(meta.size),
        thumbnail: ensureNullable(meta.thumbnail),
        author_image: ensureNullable(meta.author_image),
        author_image_cdn_url: ensureNullable(meta.author_image_cdn_url),
        author_image_public_id: ensureNullable(meta.author_image_public_id),
        author_image_name: ensureNullable(meta.author_image_name),
        author_image_size: ensureNullable(meta.author_image_size),
        author_image_format: ensureNullable(meta.author_image_format),
        author_image_aspect_ratio: ensureNullable(meta.author_image_aspect_ratio),
        author_image_width: ensureNullable(meta.author_image_width),
        author_image_height: ensureNullable(meta.author_image_height),
        author_image_original: ensureNullable(meta.author_image_original),
        author_image_thumbnail: ensureNullable(meta.author_image_thumbnail),
        embed: ensureNullable(meta.embed),
        quoted_url_meta: normalizeUrlMetaForCacheWrite(meta.quoted_url_meta)
    };
};

const normalizeMessageForCacheWrite = (message, toFolder, movedAt) => ({
    ...message,
    folder: toFolder || message?.folder || null,
    deletedAt: null,
    lastMovedAt: movedAt,
    aiRole: ensureNullable(message?.aiRole),
    threadReplyCount: ensureNumber(message?.threadReplyCount),
    threadChatCount: ensureNumber(message?.threadChatCount),
    threadMessageCount: ensureNumber(message?.threadMessageCount),
    threadChatUpdatedAt: ensureNullable(message?.threadChatUpdatedAt),
    threadMessageUpdatedAt: ensureNullable(message?.threadMessageUpdatedAt),
    threadActivityUpdatedAt: ensureNullable(message?.threadActivityUpdatedAt),
    suggestedFolderTitle: null,
    caption: ensureNullable(message?.caption),
    keywords: ensureArray(message?.keywords),
    urlMeta: ensureArray(message?.urlMeta).map(normalizeUrlMetaForCacheWrite).filter(Boolean)
});

/**
 * Hook for moving messages between folders
 * Uses centralized CacheManager for cache updates
 */
function useMoveMessagesMutation() {
    const { showGoToFolderNotification } = useNotificationSystem();
    const client = useApolloClient();

    const {
        state: {
            selectedFolder,
            folderFilterType,
            folderThreadActivityFilter,
            folderThreadVisibilityFilter,
            derivedThreadVisibilityFilter,
            showDeletedMessages,
            searchTerm,
            searchGlobal,
            searchDeletedMessages,
            searchFolderFilterType,
            searchThreadActivityFilter,
            searchThreadVisibilityFilter,
            searchActive,
            selectedMessage
        },
        context: { setMessagesToSend, setSelectedMessagesMap }
    } = React.useContext(AuthContext);
    const activeFolderThreadVisibilityFilter = usesDerivedThreadVisibilityFilter(selectedFolder)
        ? derivedThreadVisibilityFilter
        : folderThreadVisibilityFilter;
    const normalizedFilterSearchText = String(searchTerm || '').trim();

    const [moveMessages, { data: moveSuccess, error: moveError, loading: moveLoading }] = useMutation(
        MUTATION.MOVE_MESSAGES
    );

    /**
     * Update the single message view if it's currently selected
     */
    const updateSelectedMessage = (messages, toFolder) => {
        if (!selectedMessage) return;
        
        // Skip cache update for temp/optimistic messages (they don't exist on server)
        if (selectedMessage.id?.startsWith('temp-')) return;
        
        const isSelectedMessageBeingMoved = messages.some(m => m.id === selectedMessage.id);
        if (isSelectedMessageBeingMoved) {
            try {
                const cache = client.cache;
                const data = cache.readQuery({
                    query: QUERY.GET_MESSAGE,
                    variables: {
                        data: {
                            id: selectedMessage.id
                        }
                    }
                });

                if (data?.getMessage) {
                    cache.writeQuery({
                        query: QUERY.GET_MESSAGE,
                        variables: {
                            data: {
                                id: selectedMessage.id
                            }
                        },
                        data: {
                            getMessage: {
                                ...data.getMessage,
                                folder: toFolder,
                                suggestedFolderTitle: null
                            }
                        }
                    });
                }
            } catch (error) {
                // Message not in cache, skip
            }
        }
    };

    /**
     * Sort message IDs by creation date for consistent ordering
     * Filters out temp/optimistic messages that don't exist on server yet
     */
    const prepareMessages = messages => {
        // Filter out temp messages - they don't exist on server yet
        const realMessages = messages.filter(m => !m.id?.startsWith('temp-'));
        let sorted = [...realMessages];
        sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        return sorted.map(message => message.id);
    };

    /**
     * Track message as recently moved to prevent merge policy from re-adding it
     */
    const trackMovedMessages = (messages, fromFolderId) => {
        if (typeof window === 'undefined') return;
        
        window.__recentlyMovedMessages = window.__recentlyMovedMessages || new Map();
        
        // Get or create the set for this folder
        if (!window.__recentlyMovedMessages.has(fromFolderId)) {
            window.__recentlyMovedMessages.set(fromFolderId, new Set());
        }
        const folderSet = window.__recentlyMovedMessages.get(fromFolderId);
        
        // Add all message IDs
        messages.forEach(msg => {
            folderSet.add(msg.id);
        });
        
        // Debug logging disabled - uncomment if needed
        // if (process.env.NODE_ENV === 'development') {
        //     console.log('🏷️ Tracked moved messages:', messages.map(m => m.id), 'from folder:', fromFolderId);
        // }
        
        // Clean up after 10 seconds (enough for any network fetches to complete)
        setTimeout(() => {
            messages.forEach(msg => {
                folderSet.delete(msg.id);
            });
            // Clean up empty sets
            if (folderSet.size === 0) {
                window.__recentlyMovedMessages.delete(fromFolderId);
            }
            // Debug logging disabled
            // if (process.env.NODE_ENV === 'development') {
            //     console.log('🧹 Cleaned up tracked messages for folder:', fromFolderId);
            // }
        }, 10000);
    };

    // Track which moves have been processed to prevent duplicate cache updates
    const processedMovesRef = React.useRef(new Set());
    
    /**
     * Perform cache update for message move
     * Uses CacheManager.removeMessages (like trash/delete) to properly remove from source,
     * then adds to destination folder. This ensures Apollo's merge policies don't re-add
     * the removed messages from existing cache.
     */
    const performCacheUpdate = (messages, toFolder, currentFolder, isInitialCall = false) => {
        // Create a unique key for this move operation
        const moveKey = messages.map(m => m.id).sort().join(',') + '_' + currentFolder?.id + '_' + toFolder?.id;
        
        // Only process once per move operation
        if (processedMovesRef.current.has(moveKey) && !isInitialCall) {
            // Skip duplicate - already processed
            return;
        }
        processedMovesRef.current.add(moveKey);
        
        // Clean up after a short delay
        setTimeout(() => {
            processedMovesRef.current.delete(moveKey);
        }, 5000);
        
        const isTrash = currentFolder?.deletedAt ? true : showDeletedMessages;
        const movedAt = new Date().toISOString();
        const movedMessages = messages.map(message => {
            const normalizedMessage = normalizeMessageForCacheWrite(message, toFolder, movedAt);
            if (message?.parentMessageId) {
                return {
                    ...normalizedMessage,
                    parentMessageId: null,
                    threadRootMessageId: message.id
                };
            }
            return normalizedMessage;
        });

        // Track moved messages to prevent merge policy from re-adding them
        // Only do this when the source and destination folders differ.
        // Same-folder moves are used for thread detach flows and must remain visible immediately.
        if (isInitialCall && currentFolder?.id !== toFolder?.id) {
            trackMovedMessages(messages, currentFolder?.id);
        }

        // For global search, update the folder property in search cache BEFORE removing
        // This prevents the message from disappearing when we just want to update its folder
        if (searchActive && searchGlobal) {
            try {
                const queryData = {
                    text: normalizedFilterSearchText,
                    type: searchFolderFilterType || [],
                    folderId: '', // Global search uses empty folderId
                    isTrash: searchDeletedMessages || false,
                    sortBy: 'createdAt',
                    ...buildThreadActivityFilterInput(searchThreadActivityFilter),
                    ...buildThreadVisibilityFilterInput(searchThreadVisibilityFilter)
                };

                const existingData = client.cache.readQuery({
                    query: QUERY.SEARCH_MESSAGES,
                    variables: { data: queryData }
                });

                if (existingData?.searchMessages?.messages) {
                    const messageIds = new Set(messages.map(m => m.id));
                    const updatedMessages = existingData.searchMessages.messages.map(msg => {
                        if (messageIds.has(msg.id)) {
                            return {
                                ...msg,
                                folder: toFolder,
                                suggestedFolderTitle: null
                            };
                        }
                        return msg;
                    });

                    client.cache.writeQuery({
                        query: QUERY.SEARCH_MESSAGES,
                        variables: { data: queryData },
                        data: {
                            searchMessages: {
                                ...existingData.searchMessages,
                                messages: updatedMessages
                            }
                        }
                    });

                    if (process.env.NODE_ENV === 'development') {
                        console.log('📝 Updated folder property in global search cache:', {
                            messageCount: messages.length,
                            newFolder: toFolder?.title
                        });
                    }
                }
            } catch (error) {
                // Search cache might not exist
                if (process.env.NODE_ENV === 'development') {
                    console.log('Search cache not ready for folder update:', error);
                }
            }
        }

        const isAllViewMove = selectedFolder?.id === HIERARCHY && !showDeletedMessages && !searchActive;

        if (isAllViewMove) {
            const messageIds = new Set(messages.map(m => m.id));

            // Keep currently visible All list stable by updating active FILTER_MESSAGES cache in place.
            try {
                const allQueryData = {
                    text: normalizedFilterSearchText,
                    type: folderFilterType.length > 0 ? folderFilterType : [],
                    folderId: '',
                    isTrash: isTrash,
                    sortBy: DEFAULT_SORT_FIELD,
                    ...buildThreadActivityFilterInput(folderThreadActivityFilter),
                    ...buildThreadVisibilityFilterInput(activeFolderThreadVisibilityFilter)
                };
                const allViewData = client.cache.readQuery({
                    query: QUERY.FILTER_MESSAGES,
                    variables: { data: allQueryData }
                });

                if (allViewData?.filterMessages?.messages) {
                    const updatedAllMessages = allViewData.filterMessages.messages.map(message => {
                        if (message && messageIds.has(message.id)) {
                            return movedMessages.find(movedMessage => movedMessage.id === message.id) || message;
                        }
                        return message;
                    });

                    client.cache.writeQuery({
                        query: QUERY.FILTER_MESSAGES,
                        variables: { data: allQueryData },
                        data: {
                            filterMessages: {
                                ...allViewData.filterMessages,
                                messages: updatedAllMessages
                            }
                        }
                    });
                }
            } catch (error) {
                // All view cache might not exist yet
            }

            // Update normalized entities so every watcher sees the new folder immediately.
            movedMessages.forEach(message => {
                CacheManager.updateSingleMessage(client, message.id, {
                    folder: toFolder,
                    lastMovedAt: movedAt,
                    deletedAt: null,
                    suggestedFolderTitle: null,
                    ...(message?.parentMessageId == null
                        ? {
                              parentMessageId: null,
                              threadRootMessageId: message.id
                          }
                        : {})
                });
            });

            // Remove moved messages from source folder cache if that list exists.
            if (currentFolder?.id && currentFolder.id !== toFolder?.id) {
                try {
                    const sourceVariables = {
                        data: {
                            folderId: currentFolder.id,
                            isTrash: isTrash,
                            ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                        }
                    };
                    const sourceData = client.cache.readQuery({
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables: sourceVariables
                    });

                    if (sourceData?.getFolderContent?.messages?.messages) {
                        const filteredSourceMessages = sourceData.getFolderContent.messages.messages.filter(
                            message => message && !messageIds.has(message.id)
                        );

                        client.cache.writeQuery({
                            query: QUERY.GET_FOLDER_CONTENT,
                            variables: sourceVariables,
                            data: {
                                getFolderContent: {
                                    ...sourceData.getFolderContent,
                                    messages: {
                                        ...sourceData.getFolderContent.messages,
                                        messages: filteredSourceMessages
                                    }
                                }
                            }
                        });
                    }
                } catch (error) {
                    // Source folder cache entry might not exist
                }
            }

            // Add to destination folder cache (if destination folder query is already cached).
            CacheManager.addMessagesToFolder(
                client,
                movedMessages,
                toFolder,
                isTrash,
                folderThreadVisibilityFilter
            );

            // Update selected message view if needed
            updateSelectedMessage(movedMessages, toFolder);
        } else {
            // Step 1: Remove messages from source folder using removeMessages
            // This uses cache.evict() which bypasses Apollo's merge function that would otherwise
            // re-add the messages from existing cache
            // Note: For global search, we skip normalized eviction since we want to keep the message
            // in the search cache with just an updated folder property
            CacheManager.removeMessages(client, messages, {
                folderId: currentFolder?.id,
                isTrash: isTrash,
                filterType: folderFilterType,
                isSearch: searchActive && !searchGlobal, // Skip search removal for global search
                searchTerm: normalizedFilterSearchText,
                searchGlobal: searchGlobal,
                searchFolderFilterType: searchFolderFilterType,
                skipSearchRemoval: true,
                skipNormalizedEviction: searchActive // Don't evict during search (local or global) so the moved message stays visible with its updated folder
            });

            // Step 2: Add messages to destination folder cache (if it exists in cache)
            // This ensures the message appears immediately when navigating to the destination folder
            CacheManager.addMessagesToFolder(
                client,
                movedMessages,
                toFolder,
                isTrash,
                folderThreadVisibilityFilter
            );

            // Keep normalized entities aligned with server-side thread detach semantics.
            movedMessages.forEach(message => {
                CacheManager.updateSingleMessage(client, message.id, {
                    folder: toFolder,
                    lastMovedAt: movedAt,
                    deletedAt: null,
                    suggestedFolderTitle: null,
                    ...(message?.parentMessageId == null
                        ? {
                              parentMessageId: null,
                              threadRootMessageId: message.id
                          }
                        : {})
                });
            });

            // Update selected message view if needed
            updateSelectedMessage(movedMessages, toFolder);
        }

        // Dispatch event to update memory refs in FolderContainer (only on initial call)
        if (isInitialCall) {
            CacheManager.dispatchEvent('messagesMoved', {
                messageIds: messages.map(m => m.id),
                fromFolderId: currentFolder?.id,
                toFolderId: toFolder?.id,
                toFolder: toFolder, // Include full folder object for cache updates
                movedMessages
            });
        }
    };

    /**
     * Main function to move messages
     */
    const runThis = async (messages, toFolder, currentFolder) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('📦 useMoveMessages: Moving', messages.length, 'messages to', toFolder?.title);
        }

        const resolvedCurrentFolder = currentFolder || messages?.[0]?.folder || selectedFolder || toFolder;

        if (!messages || !toFolder || !resolvedCurrentFolder) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Missing required parameters for move operation');
            }
            return;
        }

        // Show notification if moving to a different folder
        if (selectedFolder?.id !== toFolder.id) {
            showGoToFolderNotification(toFolder);
        }

        // Optimistically update cache IMMEDIATELY for instant UI feedback
        try {
            performCacheUpdate(messages, toFolder, resolvedCurrentFolder, true); // isInitialCall = true
        } catch (optimisticError) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Optimistic update failed, will rely on mutation update callback:', optimisticError);
            }
        }

        const messageIds = prepareMessages(messages);
        
        // If all messages were temp messages (filtered out), nothing to send to server
        if (messageIds.length === 0) {
            if (process.env.NODE_ENV === 'development') {
                console.log('📦 useMoveMessages: All messages are temp, skipping server mutation');
            }
            setMessagesToSend([]);
            setSelectedMessagesMap({});
            return;
        }
        
        const isLargeMove = messageIds.length >= 50;
        const variables = {
            data: {
                messageIds: messageIds,
                toFolder: toFolder.id
            }
        };

        if (isLargeMove) {
            // For large moves, favor refetching instead of many cache mutations
            await moveMessages({
                variables,
                refetchQueries: [
                    {
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables: {
                            data: {
                                folderId: resolvedCurrentFolder.id,
                                isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                            }
                        }
                    },
                    {
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables: {
                            data: {
                                folderId: toFolder.id,
                                isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                            }
                        }
                    }
                ],
                awaitRefetchQueries: true
            });
        } else {
            // For smaller moves, use optimistic updates
            moveMessages({
                variables,
                optimisticResponse: {
                    moveMessages: true
                },
                update: (store, { data: { moveMessages } }) => {
                    try {
                        // Run cache update again to ensure consistency after server response
                        performCacheUpdate(messages, toFolder, resolvedCurrentFolder);
                        
                        if (process.env.NODE_ENV === 'development') {
                            console.log('✅ Move mutation completed, cache updated');
                        }
                    } catch (error) {
                        if (process.env.NODE_ENV === 'development') {
                            console.error('Cache update failed during move:', error);
                        }
                        // Trigger refetch on error
                        client.refetchQueries({
                            include: [
                                {
                                    query: QUERY.GET_FOLDER_CONTENT,
                                    variables: {
                                        data: {
                                            folderId: resolvedCurrentFolder.id,
                                            isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                            ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                                        }
                                    }
                                },
                                {
                                    query: QUERY.GET_FOLDER_CONTENT,
                                    variables: {
                                        data: {
                                            folderId: toFolder.id,
                                            isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                            ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                                        }
                                    }
                                }
                            ]
                        }).catch(refetchError => {
                            if (process.env.NODE_ENV === 'development') {
                                console.error('Failed to refetch after cache update error:', refetchError);
                            }
                        });
                    }
                },
                onError: (error) => {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Move mutation failed:', error);
                    }
                    // Refetch on error to recover
                    client.refetchQueries({
                        include: [
                            {
                                query: QUERY.GET_FOLDER_CONTENT,
                                variables: {
                                    data: {
                                        folderId: resolvedCurrentFolder.id,
                                        isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                        ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                                    }
                                }
                            },
                            {
                                query: QUERY.GET_FOLDER_CONTENT,
                                variables: {
                                    data: {
                                        folderId: toFolder.id,
                                        isTrash: resolvedCurrentFolder.deletedAt ? true : showDeletedMessages,
                                        ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                                    }
                                }
                            }
                        ]
                    }).catch(() => undefined);
                }
            });
        }

        setMessagesToSend([]);
        setSelectedMessagesMap({});
    };

    return [runThis, moveSuccess];
}

export default useMoveMessagesMutation;
